import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { RocketLaunchIcon, SpinnerGapIcon, LightningIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { fetchPumpLaunches } from "../lib/api";
import { TradeButton } from "../components/TradeButton";
import { AlphaBadge, useAlpha } from "../components/AlphaBadge";
import { HitRateBadge, useDeployerStatsBatch } from "../components/HitRateBadge";
import { useTrustedCreators } from "../lib/useTrustedCreators";
import { useAlphaChime } from "../lib/alphaSound";
import { fmtUsd, fmtPrice } from "../lib/format";
import { shortAddr, timeAgo } from "../lib/solana";

const SORTS = [
  { key: "curve", label: "Curve %" },
  { key: "velocity", label: "Buy velocity" },
  { key: "new", label: "Newest" },
];

const curveColor = (p) => (p >= 80 ? "#00E599" : p >= 40 ? "#F5A623" : "#8b8b8b");

const Fallback = ({ symbol }) => (
  <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground shrink-0">
    {(symbol || "?").slice(0, 3)}
  </div>
);

function LaunchCard({ t, ds }) {
  const coin = { address: t.mint, symbol: t.symbol, creator: t.creator, url: t.url };
  const c = t.curveProgress || 0;
  const alpha = useAlpha()(t.creator);
  return (
    <div data-testid={`launch-card-${t.mint}`} className={`border bg-card p-4 space-y-3 ${alpha ? "border-[#C9A227]/50 bg-[#C9A227]/[0.06]" : ds?.proven ? "border-neon/50" : c >= 80 ? "border-neon/40" : "border-white/10"}`}>
      <div className="flex items-start gap-3">
        {t.image ? (
          <img src={t.image} alt={t.symbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <Fallback symbol={t.symbol} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <div className="font-heading font-bold text-base truncate">{t.symbol || shortAddr(t.mint)}</div>
            <AlphaBadge creator={t.creator} compact />
            <HitRateBadge stats={ds} compact />
          </div>
          <div className="text-xs text-muted-foreground truncate">{t.name || shortAddr(t.mint)}</div>
        </div>
        <div className="text-right shrink-0">
          <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(t.createdTs)}</span>
          {!t.indexed && <div className="font-mono text-[9px] text-warn mt-0.5">indexing…</div>}
        </div>
      </div>

      {/* Bonding curve progress */}
      <div>
        <div className="flex items-center justify-between text-[10px] mb-1">
          <span className="uppercase tracking-wider text-muted-foreground">Bonding curve</span>
          <span className="font-mono font-bold" style={{ color: curveColor(c) }}>{c}%</span>
        </div>
        <div className="h-2 bg-white/5 overflow-hidden">
          <div className="h-full transition-all duration-500" style={{ width: `${c}%`, background: curveColor(c) }} />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Buys 5m</div>
          <div className="font-mono font-bold text-neon text-sm mt-0.5">{t.buys5}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Vol 1h</div>
          <div className="font-mono text-sm mt-0.5">{t.volH1 != null ? fmtUsd(t.volH1) : "—"}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">MCap</div>
          <div className="font-mono text-sm mt-0.5">{t.marketCapUsd != null ? fmtUsd(t.marketCapUsd) : "—"}</div>
        </div>
      </div>

      <div className="pt-1">
        <TradeButton coin={coin} size="sm" />
      </div>
    </div>
  );
}

export default function Launches() {
  const [sort, setSort] = useState("curve");
  const [provenOnly, setProvenOnly] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["pumpLaunches", sort],
    queryFn: () => fetchPumpLaunches(sort),
    refetchInterval: 15000,
  });

  const launches = data?.launches || [];
  const stats = useDeployerStatsBatch(launches.map((t) => t.creator));
  const shown = provenOnly ? launches.filter((t) => stats[t.creator]?.proven) : launches;
  const { isAlpha } = useTrustedCreators();
  useAlphaChime(launches.filter((t) => isAlpha(t.creator)).map((t) => t.mint));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 flex items-center justify-center bg-neon text-black">
              <RocketLaunchIcon size={24} weight="bold" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">LAUNCHPAD</h1>
          </div>
          <p className="text-muted-foreground mt-3 text-sm">
            Live <span className="text-neon font-semibold">pump.fun</span> launches before they migrate — sorted by bonding-curve progress &amp; buy velocity.
            {data?.tracking != null && <span className="font-mono text-xs"> · tracking {data.tracking} live</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="launch-proven-only"
            onClick={() => setProvenOnly((v) => !v)}
            title="Show only launches from deployers with a past $1M+ runner"
            className={`px-4 py-2.5 text-sm font-semibold border transition-colors ${provenOnly ? "border-neon/60 bg-neon/10 text-neon" : "border-white/10 text-muted-foreground hover:text-white"}`}
          >
            🎯 Proven only{provenOnly ? ` (${shown.length})` : ""}
          </button>
          <div className="flex border border-white/10">
          {SORTS.map((s) => (
            <button
              key={s.key}
              data-testid={`launch-sort-${s.key}`}
              onClick={() => setSort(s.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors ${
                sort === s.key ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Streaming fresh launches…</span>
        </div>
      ) : shown.length === 0 ? (
        <div className="border border-white/10 bg-card py-24 flex flex-col items-center gap-4 text-center">
          <RocketLaunchIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-md">
            {provenOnly
              ? "No live launches from proven deployers right now — they're rarer by design. Turn the filter off to see the full stream, or lean on the ⭐ Proven deployer launch Telegram alert to get pinged the instant one mints."
              : "Waiting for fresh pump.fun launches to stream in… hang tight, new coins mint constantly."}
          </p>
        </div>
      ) : (
        <div data-testid="launches-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((t) => <LaunchCard key={t.mint} t={t} ds={stats[t.creator]} />)}
        </div>
      )}

      <div className="border border-white/10 bg-card p-4 flex gap-3">
        <LightningIcon size={18} className="text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Live feed via PumpPortal. Curve % &amp; volume enriched from DexScreener once a coin is indexed. Brand-new mints show a rough estimate until data catches up. High-risk, high-speed — not financial advice.
        </p>
      </div>
    </div>
  );
}
